import { Text } from '@fluentui/react-components';
import { ToolboxRegular } from '@fluentui/react-icons';
import cn from 'classnames';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CommunicationMessage,
  Illustrations,
  IllustrationSize,
} from '../../Communication/CommunicationMessage';
import { WidgetsHeadline } from '../WidgetsHeadline/WidgetsHeadline';
import { AssignmentItem } from './AssignmentsWidget.interfaces';
import './AssignmentsWidget.scss';
import { AssignmentsWidgetList } from './AssignmentsWidgetList';
import { AssignmentsWidgetSkeleton } from './AssignmentsWidgetSkeleton';

export interface AssignmentsWidgetProps {
  isLoading: boolean;
  lateAssignments: AssignmentItem[];
  upcomingAssignments: AssignmentItem[];
  /**
   * Primary click handler. When omitted, the assignment rows are inert (not
   * focusable, no click). Teams omits this and passes {@link onOpenInBrowser}
   * instead, so clicking a row never springboards the user out of Teams.
   */
  onAssignmentSelected?: (assignment: AssignmentItem) => void;
  /**
   * When set, each row renders a ⋯-menu with an "open in browser" action.
   * Teams passes an `app.openLink`-based handler here.
   */
  onOpenInBrowser?: (assignment: AssignmentItem) => void;
  classNames?: string;
  useDarkIllustration?: boolean;
  error?: Error | null;
}

export const AssignmentsWidget: FC<AssignmentsWidgetProps> = ({
  isLoading,
  lateAssignments,
  upcomingAssignments,
  onAssignmentSelected,
  onOpenInBrowser,
  classNames,
  useDarkIllustration = false,
  error,
}) => {
  const { t } = useTranslation();

  const hasNoAssignments = upcomingAssignments.length === 0 && lateAssignments.length === 0;

  return (
    <div
      data-cy="assignmentsWidget"
      className={cn('assignmentsWidget', { 'assignmentsWidget--error': error }, classNames)}
    >
      <WidgetsHeadline
        content={t('dashboard:my_assignments', { defaultValue: 'Zu erledigende Aufgaben' })}
        icon={<ToolboxRegular fontSize={24} />}
      />
      {isLoading && <AssignmentsWidgetSkeleton />}
      {!isLoading && error && (
        <Text className="text-error">
          {t('assignments:assignment_load_error', {
            defaultValue: 'Fehler beim Laden der Aufgaben',
          })}
        </Text>
      )}
      {!isLoading && !error && (
        <>
          {hasNoAssignments && (
            <CommunicationMessage
              illustration={Illustrations.Empty}
              headline=""
              text={t('assignments:assignment_assigned_empty_headline', {
                defaultValue: 'Keine zugewiesenen Aufgaben vorhanden',
              })}
              illustrationSize={IllustrationSize.Small}
              useDarkIllustration={useDarkIllustration}
            />
          )}
          {lateAssignments.length > 0 && (
            <>
              <h3>
                {t('dashboard:my_assignments_late_headline', {
                  defaultValue: 'Verspätete Aufgaben',
                })}
              </h3>
              <div className="listWrapper">
                <AssignmentsWidgetList
                  assignments={lateAssignments}
                  onAssignmentSelected={onAssignmentSelected}
                  onOpenInBrowser={onOpenInBrowser}
                />
              </div>
            </>
          )}
          {upcomingAssignments.length > 0 && (
            <>
              <h3 className="lateHeadline">
                {t('dashboard:my_assignments_upcomming_headline', {
                  defaultValue: 'Anstehende Aufgaben',
                })}
              </h3>
              <div className="listWrapper">
                <AssignmentsWidgetList
                  assignments={upcomingAssignments}
                  onAssignmentSelected={onAssignmentSelected}
                  onOpenInBrowser={onOpenInBrowser}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
